import { faInfoCircle, faPlus } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Container, Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material"
import { ColumnDef } from "@tanstack/react-table"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { DefaultColumnFilter, ServerSideMultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import TableDropdown from "components/ClpTable/components/actions/TableDropdown/TableDropdown"
import TableLink from "components/ClpTable/components/actions/TableLink/TableLink"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"
import ClpTableServerSidePaging from "components/ClpTable/ServerSidePaging"
import { TabPanel } from "components/navigation/TabPanel"
import ChipInfoIcon from "components/Status/ChipInfo/ChipInfoIcon"
import ChipPriority from "components/Status/ChipInfo/ChipPriority"
import SubTabs from "components/SubTabs"
import { ViewToggler } from "components/ViewToggler/ViewToggler"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import moment from "moment"
import {
  Deviation,
  DeviationAttributes,
  deviationChip,
  DeviationTableFilterOptions
} from "pages/deviation/deviation.interface"
import { prio } from "pages/issuereports/pages/archive/components/table/helpers"
import { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useHistory, useLocation } from "react-router-dom"
import { deviationType } from "shared/interfaces/deviation"
import { StyledFontAwesomeIconForButton } from "shared/styled/General"
import { StyledTableContainer } from "shared/styled/Global"
import { ActionType } from "shared/vehicle/components/InspectionBookingSidebar.interfaces"
import { ModalDialog } from "shared/vehicle/components/ModalDialog"
import ExcelExportSidebar from "../components/ExcelExportSidebar"
import { NumberTypeSelector } from "components/ClpTable/components/NumberTypeSelector"

const queryName = "type"

const initialModalData = {
  message: "",
  title: "",
  isOpen: false,
  actionType: ActionType.Cancel
}

const isDeviationAdmin = (childFeatures: string[], roles: string[]) =>
  childFeatures.find((feature) => roles.find((role) => role === feature + "Admin"))

enum DeviationView {
  Active = "active",
  Completed = "completed"
}

const viewList = (t: any) => {
  return [
    { label: t("global.active2", "Active"), value: DeviationView.Active },
    { label: t("global.history", "Completed"), value: DeviationView.Completed }
  ]
}

const DeviationList = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const appContext = useContext(AppContext)
  const [filterOptionsResult, filterOptionsApi] = useApi() as [IApiResult, IApiFunc]
  const [modalData, setModalData] = useState(initialModalData)
  const [_, singleDeviationPutApi] = useApi() as [IApiResult, IApiFunc]
  const [updatedDeviationAttribute, setUpdatedDeviationAttribute] = useState<DeviationAttributes>({
    id: -1,
    priority: -1
  })
  const [refreshKey, setRefreshKey] = useState(0)
  const priorityOptions = [0, 1, 2, 3].map((value) => ({ value, name: prio(value, t) }))
  const [filterOptions, setFilterOptions] = useState<DeviationTableFilterOptions>({
    assignedUserOptions: [],
    deviationType: []
  })
  const [viewByTab, setViewByTab] = useState<Record<string, DeviationView>>({
    general: DeviationView.Active,
    vehicleDamage: DeviationView.Active,
    trafficLog: DeviationView.Active
  })
  const [visitedViewsByTab, setVisitedViewsByTab] = useState<Record<string, Set<DeviationView>>>({
    general: new Set([DeviationView.Active]),
    vehicleDamage: new Set([DeviationView.Active]),
    trafficLog: new Set([DeviationView.Active])
  })
  const theme = useTheme()
  const [numberType, setNumberType] = useState("reg");
  
  useEffect(() => {
    if (filterOptionsResult.status !== 2) return
    if (filterOptionsResult.data) {
      setFilterOptions(filterOptionsResult.data as DeviationTableFilterOptions)
    }
  }, [filterOptionsResult])

  useEffect(() => {
    filterOptionsApi.get(apiEndpoints.deviation.getfilteroptions)
  }, [filterOptionsApi])

  const initialColumnData: ColumnDef<Deviation>[] = useMemo(
    () => [
      {
        accessorKey: "customerDeviationId",
        header: t("global.table.id", "ID"),
        cell: ({ cell }: any) => {
          const { id, customerDeviationId } = cell.row.original
          return <TableLink label={customerDeviationId} to={allRoutes.DeviationListDetails(id)} />
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "deviationType",
        header: t("global.features.title.deviation", "Deviation"),
        accessorFn: (row: any) => {
          const formTemplate = row?.formResult?.formTemplate
          return t(`deviation.form_key.${formTemplate?.key}`, formTemplate?.name)
        },
        meta: {
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterOptions: filterOptions.deviationType.map((d) => ({
            name: t(`deviation.form_key.${d}`, d),
            value: d
          }))
        }
      },
      {
        accessorKey: "createdTime",
        header: t("global.created", "Created"),
        cell: ({ cell }: any) => {
          return cell.row.original.createdTime
            ? moment.utc(cell.row.original.createdTime).local().format("YYYY-MM-DD HH:mm")
            : "-"
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "involvedUserName",
        header: t("deviation.involved", "Created"),
        filterFn: "arrIncludesSome",
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "updatedTime",
        header: t("global.managed", "Managed"),
        cell: ({ cell }: any) => {
          return cell.row.original.updatedTime
            ? moment.utc(cell.row.original.updatedTime).local().format("YYYY-MM-DD HH:mm")
            : "-"
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "updatedByName",
        header: t("global.managed_by", "Managed by"),
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "assignedUser",
        header: t("global.assigned", "Assigned"),
        filterFn: "arrIncludesSome",
        meta: {
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterOptions: filterOptions.assignedUserOptions.map((u) => ({ name: u, value: u }))
        }
      },
      {
        accessorFn: (row) => row.priority,
        id: "priority",
        cell: ({ cell }: any) => {
          const isAdmin = isDeviationAdmin(
            cell.row.original?.formResult?.formTemplate?.childFeatures,
            appContext?.appUser?.roles
          )
          return isAdmin ? (
            <TableDropdown
              initialValue={cell.row.original.priority}
              handleOnChange={(event) => {
                setModalData({
                  actionType: ActionType.Confirm,
                  isOpen: true,
                  title: t("global.change_priority", "Change priority"),
                  message: `${t(
                    "global.change_the_priority_of",
                    "Are you sure you want to change the priority of"
                  )} ${cell.row.original.name} ${t("global.from", "from")}
                ${prio(cell.row.original.priority, t)}
                ${t("global.to", "to")} ${prio(event.target.value, t)} ?`
                })
                setUpdatedDeviationAttribute({ id: cell.row.original.id, priority: event.target.value })
              }}
              options={priorityOptions}
            />
          ) : (
            <ChipPriority type={cell.row.original.priority} />
          )
        },
        header: t("global.table.priority", "Priority"),
        filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
        meta: {
          align: "left",
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterHeader: t("global.table.priority", "Priority"),
          filterOptions: [
            { name: t("global.none", "None"), value: 0 },
            { name: t("global.low", "Low"), value: 1 },
            { name: t("global.medium", "Medium"), value: 2 },
            { name: t("global.high", "High"), value: 3 }
          ]
        }
      },
      {
        accessorFn: (row) => row.status,
        id: "status",
        header: "Status",
        filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
        meta: {
          mainFilter: viewByTab.general === DeviationView.Active,
          filter: ServerSideMultiSelectFilter,
          filterHeader: "Status",
          filterOptions:
            viewByTab.general === DeviationView.Active
              ? [
                { name: t("global.new", "New"), value: 0 },
                { name: t("global.assigned", "Assigned"), value: 1 },
                { name: t("global.ongoing", "Ongoing"), value: 4 },
                { name: t("global.unmanaged", "Unmanaged"), value: 3 }
              ]
              : [{ name: t("global.completed", "Completed"), value: 2 }]
        },
        cell: ({ cell }: any) => {
          return <ChipInfoIcon type={deviationChip(cell.row.original.status)} />
        }
      },
      {
        accessorKey: "edit",
        header: "",
        meta: {
          align: "right"
        },
        cell: ({ cell }: any) => {
          const isAdmin = isDeviationAdmin(
            cell.row.original?.formResult?.formTemplate?.childFeatures,
            appContext?.appUser?.roles
          )
          return isAdmin ? (
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => history.push(allRoutes.DeviationListDetails(cell.row.original.id))}
            />
          ) : (
            <></>
          )
        }
      }
    ],
    [
      viewByTab.general,
      t,
      filterOptions.deviationType,
      filterOptions.assignedUserOptions,
      appContext?.appUser?.roles
    ]
  )

  const vehicleDamageExtraColumns: ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "customerDeviationId",
        header: t("global.table.id", "ID"),
        cell: ({ cell }: any) => {
          const { id, customerDeviationId } = cell.row.original
          return <TableLink label={customerDeviationId} to={allRoutes.DeviationListDetails(id)} />
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: numberType === "intern" ? 'internNo' : 'regNo',
        header: numberType === "intern" ? t("global.internal_number_short", "Intern No") : t("global.reg_number_short", "Reg No"),
      },
      {
        id: "dayOfIncident",
        header: t("deviation.incident_day", "Day of incident"),
        accessorFn: (row) => (row.dayOfIncident ? moment(row.dayOfIncident).format("YYYY-MM-DD HH:mm") : "-")
      },
      {
        accessorKey: "deviationType",
        header: t("global.features.title.deviation", "Deviation"),
        accessorFn: (row: any) => {
          const formTemplate = row?.formResult?.formTemplate
          return t(`deviation.form_key.${formTemplate?.key}`, formTemplate?.name)
        },
        meta: {
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterOptions: filterOptions.deviationType.map((d) => ({
            name: t(`deviation.form_key.${d}`, d),
            value: d
          }))
        }
      },
      {
        accessorKey: "createdTime",
        header: t("global.created", "Created"),
        cell: ({ cell }: any) => {
          return cell.row.original.createdTime
            ? moment.utc(cell.row.original.createdTime).local().format("YYYY-MM-DD HH:mm")
            : "-"
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "involvedUserName",
        header: t("deviation.involved", "Created"),
        filterFn: "arrIncludesSome",
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "updatedTime",
        header: t("global.managed", "Managed"),
        cell: ({ cell }: any) => {
          return cell.row.original.updatedTime
            ? moment.utc(cell.row.original.updatedTime).local().format("YYYY-MM-DD HH:mm")
            : "-"
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "updatedByName",
        header: t("global.managed_by", "Managed by"),
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "assignedUser",
        header: t("global.assigned", "Assigned"),
        filterFn: "arrIncludesSome",
        meta: {
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterOptions: filterOptions.assignedUserOptions.map((u) => ({ name: u, value: u }))
        }
      },
      {
        accessorKey: "damageCost",
        header: t("global.damage_cost", "Damage cost")
      },
      {
        accessorKey: "damageNumber",
        header: t("global.damage_number", "Damage number")
      },
      {
        accessorFn: (row) => row.priority,
        id: "priority",
        cell: ({ cell }: any) => {
          const isAdmin = isDeviationAdmin(
            cell.row.original?.formResult?.formTemplate?.childFeatures,
            appContext?.appUser?.roles
          )
          return isAdmin ? (
            <TableDropdown
              initialValue={cell.row.original.priority}
              handleOnChange={(event) => {
                setModalData({
                  actionType: ActionType.Confirm,
                  isOpen: true,
                  title: t("global.change_priority", "Change priority"),
                  message: `${t(
                    "global.change_the_priority_of",
                    "Are you sure you want to change the priority of"
                  )} ${cell.row.original.name} ${t("global.from", "from")}
                ${prio(cell.row.original.priority, t)}
                ${t("global.to", "to")} ${prio(event.target.value, t)} ?`
                })
                setUpdatedDeviationAttribute({ id: cell.row.original.id, priority: event.target.value })
              }}
              options={priorityOptions}
            />
          ) : (
            <ChipPriority type={cell.row.original.priority} />
          )
        },
        header: t("global.table.priority", "Priority"),
        filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
        meta: {
          align: "left",
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterHeader: t("global.table.priority", "Priority"),
          filterOptions: [
            { name: t("global.none", "None"), value: 0 },
            { name: t("global.low", "Low"), value: 1 },
            { name: t("global.medium", "Medium"), value: 2 },
            { name: t("global.high", "High"), value: 3 }
          ]
        }
      },
      {
        accessorFn: (row) => row.status,
        id: "status",
        header: "Status",
        filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
        meta: {
          mainFilter: viewByTab.vehicleDamage === DeviationView.Active,
          filter: ServerSideMultiSelectFilter,
          filterHeader: "Status",
          filterOptions:
            viewByTab.vehicleDamage === DeviationView.Active
              ? [
                { name: t("global.new", "New"), value: 0 },
                { name: t("global.assigned", "Assigned"), value: 1 },
                { name: t("global.ongoing", "Ongoing"), value: 4 },
                { name: t("global.unmanaged", "Unmanaged"), value: 3 }
              ]
              : [{ name: t("global.completed", "Completed"), value: 2 }]
        },
        cell: ({ cell }: any) => {
          return <ChipInfoIcon type={deviationChip(cell.row.original.status)} />
        }
      },
      {
        accessorKey: "edit",
        header: "",
        meta: {
          align: "right"
        },
        cell: ({ cell }: any) => {
          const isAdmin = isDeviationAdmin(
            cell.row.original?.formResult?.formTemplate?.childFeatures,
            appContext?.appUser?.roles
          )
          return isAdmin ? (
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => history.push(allRoutes.DeviationListDetails(cell.row.original.id))}
            />
          ) : (
            <></>
          )
        }
      }
    ],
    [
      numberType,
      viewByTab.vehicleDamage,
      t,
      filterOptions.deviationType,
      filterOptions.assignedUserOptions,
      appContext?.appUser?.roles
    ]
  )

  const trafficLogExtraColumns: ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "customerDeviationId",
        header: t("global.table.id", "ID"),
        cell: ({ cell }: any) => {
          const { id, customerDeviationId } = cell.row.original
          return <TableLink label={customerDeviationId} to={allRoutes.DeviationListDetails(id)} />
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        id: "deviationType",
        header: t("global.features.title.deviation", "Deviation"),
        accessorFn: (row: any) => {
          const formTemplate = row?.formResult?.formTemplate
          return t(`deviation.form_key.${formTemplate?.key}`, formTemplate?.name)
        },
        meta: {
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterOptions: filterOptions.deviationType.map((d) => ({
            name: t(`deviation.form_key.${d}`, d),
            value: d
          }))
        }
      },
      {
        accessorKey: "createdTime",
        header: t("global.created", "Created"),
        cell: ({ cell }: any) => {
          return cell.row.original.createdTime
            ? moment.utc(cell.row.original.createdTime).local().format("YYYY-MM-DD HH:mm")
            : "-"
        },
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "involvedUserName",
        header: t("deviation.involved", "Created"),
        filterFn: "arrIncludesSome",
        meta: { filter: DefaultColumnFilter }
      },
      {
        accessorKey: "assignedUser",
        header: t("global.assigned", "Assigned"),
        filterFn: "arrIncludesSome",
        meta: {
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterOptions: filterOptions.assignedUserOptions.map((u) => ({ name: u, value: u }))
        }
      },
      {
        accessorKey: "trafficLogTypeOfDamage",
        header: t("deviation.traffic_log_type", "Trafficlog type"),
        minSize: 140,
        cell: ({ cell }: any) => {
          const value = cell.row.original.trafficLogTypeOfDamage
          const translatedValue = value !== null ? t(`deviation.${value}`, value) : null
          return translatedValue ? translatedValue : "-"
        }
      },
      {
        accessorKey: "trafficLogTypeOfDamageType",
        header: t("deviation.type", "Type"),
        cell: ({ cell }: any) => {
          const value = cell.row.original.trafficLogTypeOfDamageType
          const translatedValue = value !== null ? t(`deviation.${value}`, value) : null
          return translatedValue ? translatedValue : "-"
        }
      },
      {
        accessorKey: "trafficLogTypeOfStaffPerson",
        header: t("deviation.person", "Person")
      },
      {
        accessorKey: "trafficLogEventDescription",
        header: t("global_event_description", "Event description")
      },
      {
        id: "trafficLogDate",
        header: t("global.date", "Date"),
        accessorFn: (row) =>
          row.trafficLogDate ? moment(row.trafficLogDate).format("YYYY-MM-DD HH:mm") : "-"
      },
      {
        header: t("global.impact", "Impact"),
        // meta: {
        //   align: "left",
        //   mainFilter: true,
        //   filter: ServerSideMultiSelectFilter,
        //   filterHeader: t("global.impact", "Impact"),
        //   filterOptions: [
        //     {
        //       name: t("deviation.incorrect_vehicle_type", "Incorrect vehicle type"),
        //       value: "true",
        //     },
        //     {
        //       name: t("deviation.reinforcement", "Reinforcement"),
        //       value: "true",
        //     },
        //     {
        //       name: t("deviation.rerouting", "Rerouting"),
        //       value: "true"
        //     },
        //     {
        //       name: t("deviation.transfer_to_bus", "Transfer to bus"),
        //       value: "true",
        //     },
        //     {
        //       name: t("deviation.other", "Other"),
        //       value: "true",
        //     },
        //     {
        //       name: t("deviation.none", "None"),
        //       value: "true",
        //     },
        //   ],
        // },
        // cell: ({ row }) => {
        cell: ({ row }) => {
          const {
            trafficLogImpactIncorrectVehicleType,
            trafficLogImpactReinforcement,
            trafficLogImpactRerouting,
            trafficLogImpactTransferToBus,
            trafficLogImpactDelay,
            trafficLogImpactOther,
            trafficLogImpactNone
          } = row.original
          const trafficLogTypes = []
          if (trafficLogImpactIncorrectVehicleType === "true") {
            trafficLogTypes.push(t("deviation.incorrect_vehicle_type", "Incorrect vehicle type"))
          }
          if (trafficLogImpactReinforcement === "true") {
            trafficLogTypes.push(t("deviation.reinforcement", "Reinforcement"))
          }
          if (trafficLogImpactRerouting === "true") {
            trafficLogTypes.push(t("deviation.rerouting", "Rerouting"))
          }
          if (trafficLogImpactTransferToBus === "true") {
            trafficLogTypes.push(t("deviation.transfer_to_bus", "Transfer to bus"))
          }
          if (trafficLogImpactDelay === "true") {
            trafficLogTypes.push(t("deviation.delay", "Delay"))
          }
          if (trafficLogImpactOther === "true") {
            trafficLogTypes.push(t("deviation.other", "Other"))
          }
          if (trafficLogImpactNone === "true") {
            trafficLogTypes.push(t("deviation.none", "None"))
          }

          if (trafficLogTypes.length === 0) return "-"

          const fullText = trafficLogTypes.join(", ")
          const isStringTooLong = fullText.length > 30

          return (
            <Box display="flex" alignItems="center">
              {isStringTooLong ? `${fullText.substring(0, 30)}...` : fullText}

              {isStringTooLong && (
                <Tooltip
                  title={fullText}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: "13px"
                      }
                    }
                  }}>
                  <IconButton size="small">
                    <FontAwesomeIcon
                      size="1x"
                      icon={faInfoCircle}
                      color={(theme.palette as any).primary[700]}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        }
      },
      {
        header: t("deviation.cancelled", "Cancelled"),
        // meta: {
        //   mainFilter: true,
        //   filter: ServerSideMultiSelectFilter,
        //   filterHeader: t("deviation.cancelled", "Cancelled"),
        //   filterOptions: [
        //     {
        //       name: t("global.cancelled", "Cancelled"),
        //       value: "true",
        //     },
        //     {
        //       name: t("deviation.cancel_in_advance", "Cancelled in advance"),
        //       value: "true",
        //     },
        //     {
        //       name: t("deviation.impact_none", "No Impact"),
        //       value: "false",
        //     },
        //   ],
        // },
        cell: ({ row }) => {
          const { trafficLogCancelled, trafficLogCancelledInAdvance, trafficLogPartiallyCancelled } =
            row.original

          const cancelMethods = []
          if (trafficLogCancelled === "true") {
            cancelMethods.push(t("deviation.cancelled", "Cancelled"))
          }
          if (trafficLogCancelledInAdvance === "true") {
            cancelMethods.push(t("deviation.cancel_in_advance", "Cancelled in advance"))
          }
          if (trafficLogPartiallyCancelled === "true") {
            cancelMethods.push(t("deviation.partially_cancelled", "Partially Cancelled"))
          }

          if (cancelMethods.length === 0) return "-"

          const fullText = cancelMethods.join(", ")
          const isStringTooLong = fullText.length > 30

          return (
            <Box display="flex" alignItems="center">
              {isStringTooLong ? `${fullText.substring(0, 30)}...` : fullText}

              {isStringTooLong && (
                <Tooltip
                  title={fullText}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: "13px"
                      }
                    }
                  }}>
                  <IconButton size="small">
                    <FontAwesomeIcon
                      size="1x"
                      icon={faInfoCircle}
                      color={(theme.palette as any).primary[700]}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        }
      },
      {
        accessorFn: (row) => row.priority,
        id: "priority",
        cell: ({ cell }: any) => {
          const isAdmin = isDeviationAdmin(
            cell.row.original?.formResult?.formTemplate?.childFeatures,
            appContext?.appUser?.roles
          )
          return isAdmin ? (
            <TableDropdown
              initialValue={cell.row.original.priority}
              handleOnChange={(event) => {
                setModalData({
                  actionType: ActionType.Confirm,
                  isOpen: true,
                  title: t("global.change_priority", "Change priority"),
                  message: `${t(
                    "global.change_the_priority_of",
                    "Are you sure you want to change the priority of"
                  )} ${cell.row.original.name} ${t("global.from", "from")}
                ${prio(cell.row.original.priority, t)}
                ${t("global.to", "to")} ${prio(event.target.value, t)} ?`
                })
                setUpdatedDeviationAttribute({ id: cell.row.original.id, priority: event.target.value })
              }}
              options={priorityOptions}
            />
          ) : (
            <ChipPriority type={cell.row.original.priority} />
          )
        },
        header: t("global.table.priority", "Priority"),
        filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
        meta: {
          align: "left",
          mainFilter: true,
          filter: ServerSideMultiSelectFilter,
          filterHeader: t("global.table.priority", "Priority"),
          filterOptions: [
            { name: t("global.none", "None"), value: 0 },
            { name: t("global.low", "Low"), value: 1 },
            { name: t("global.medium", "Medium"), value: 2 },
            { name: t("global.high", "High"), value: 3 }
          ]
        }
      },
      {
        accessorFn: (row) => row.status,
        id: "status",
        header: "Status",
        filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
        meta: {
          mainFilter: viewByTab.trafficLog === DeviationView.Active,
          filter: ServerSideMultiSelectFilter,
          filterHeader: "Status",
          filterOptions:
            viewByTab.trafficLog === DeviationView.Active
              ? [
                { name: t("global.new", "New"), value: 0 },
                { name: t("global.assigned", "Assigned"), value: 1 },
                { name: t("global.ongoing", "Ongoing"), value: 4 },
                { name: t("global.unmanaged", "Unmanaged"), value: 3 }
              ]
              : [{ name: t("global.completed", "Completed"), value: 2 }]
        },
        cell: ({ cell }: any) => {
          return <ChipInfoIcon type={deviationChip(cell.row.original.status)} />
        }
      },
      {
        accessorKey: "edit",
        header: "",
        meta: {
          align: "right"
        },
        cell: ({ cell }: any) => {
          const isAdmin = isDeviationAdmin(
            cell.row.original?.formResult?.formTemplate?.childFeatures,
            appContext?.appUser?.roles
          )
          return isAdmin ? (
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => history.push(allRoutes.DeviationListDetails(cell.row.original.id))}
            />
          ) : (
            <></>
          )
        }
      }
    ],
    [
      viewByTab.trafficLog,
      t,
      filterOptions.deviationType,
      filterOptions.assignedUserOptions,
      appContext?.appUser?.roles,
      theme.palette
    ]
  )

  const deviationTabsConfig = (t: any) => [
    {
      index: 0,
      enabled: true,
      value: "all",
      label: t("global.all_deviations", "All deviations")
    },
    {
      index: 1,
      enabled: appContext.appUser?.customer?.enabledFeatures?.includes(deviationType.deviationVehicleDamage),
      value: "vehicledamage",
      label: t("global.features.title.deviationvehicledamage", "Vehicle damage")
    },
    {
      index: 2,
      enabled: appContext.appUser?.customer?.enabledFeatures?.includes(deviationType.deviationTrafficLog),
      value: "trafficlog",
      label: t("global.features.title.deviationtrafficlog", "Traffic log")
    }
  ]

  const tabs = deviationTabsConfig(t)
    .filter((tab) => tab.enabled)
    .map((tab: any, index: number) => ({ ...tab, index }))

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search)
    const tabParam = params.get(queryName)
    const tabConfig = tabs.find((tab) => tab.value === tabParam)
    return tabConfig || tabs[0]
  }

  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set([getInitialTab()?.value]))

  const [tabValue, setTabValue] = useState(() => getInitialTab()?.index || 0)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tabParam = params.get(queryName)
    const tabConfig = tabs.find((tab) => tab.value === tabParam)
    setTabValue(tabConfig ? tabConfig.index : 0)
  }, [location.search, tabs])

  const columnsByTab: any = {
    general: [...initialColumnData],
    vehicleDamage: [...vehicleDamageExtraColumns],
    trafficLog: [...trafficLogExtraColumns]
  }

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue)
    const selectedTab = tabs.find((tab) => tab.index === newValue)
    if (selectedTab) {
      setVisitedTabs((prev) => new Set(prev).add(selectedTab.value))
      const params = new URLSearchParams(location.search)
      params.set(queryName, selectedTab.value)
      history.replace({ search: params.toString() })
    }
  }

  const handleViewChange = (tabKey: string) => (newView: string) => {
    const viewValue = newView as DeviationView
    setViewByTab((prev) => ({ ...prev, [tabKey]: viewValue }))
    setVisitedViewsByTab((prev) => ({
      ...prev,
      [tabKey]: new Set(prev[tabKey]).add(viewValue)
    }))
  }

  const handleRowClick = (row: any) => {
    const params = new URLSearchParams(location.search)
    history.push({
      pathname: allRoutes.DeviationListDetails(row.original?.id),
      state: { returnTab: tabValue, returnSearch: params.toString() }
    })
  }
  
  return (
    <div>
      <Container>
        <Box>
          <Box display="flex" sx={{ flexDirection: { xs: "column", md: "row" } }} gap={2}>
            <Box order={1} flexGrow={2}>
              <Grid item>
                <Typography variant="h2">{t("global.features.title.deviation", "Deviation")}</Typography>
              </Grid>
            </Box>
            <Box order={3}>
              <ExcelExportSidebar />
            </Box>
            <Box order={4}>
              <Button
                variant="contained"
                component={Link}
                to={allRoutes.DeviationListCreate()}
                color="primary">
                {t("deviation.create_deviation", "Create deviation")}
                <StyledFontAwesomeIconForButton icon={faPlus} color={theme.palette.secondary.main} />
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
      <SubTabs tabs={tabs} value={tabValue} setValue={handleTabChange} />
      <StyledTableContainer>
        <ModalDialog
          modalData={modalData}
          setModalData={setModalData}
          setShowSidebar={() => { }}
          handleSubmit={() => {
            singleDeviationPutApi.put(apiEndpoints.deviation.updateattributes, updatedDeviationAttribute)
          }}
          handleCancel={() => {
            setUpdatedDeviationAttribute({ id: -1, priority: -1 })
          }}
        />
        {visitedTabs.has("all") && (
          <Box
            sx={{ display: tabs.find((tab) => tab.value === "all")?.index === tabValue ? "block" : "none" }}>
            <TabPanel value={tabValue} key={0} index={tabs.find((tab) => tab.value === "all")?.index || 0}>
              <Box px={{ xs: 2, md: 3 }} pt={{ xs: 2, md: 3 }}>
                <ViewToggler
                  setView={handleViewChange("general")}
                  view={viewByTab.general}
                  viewList={viewList(t)}
                />
              </Box>
              {visitedViewsByTab.general.has(DeviationView.Active) && (
                <Box sx={{ display: viewByTab.general === DeviationView.Active ? "block" : "none" }}>
                  <ClpTableServerSidePaging
                    onRowClick={(row: any) => {
                      handleRowClick(row)
                    }}
                    columns={initialColumnData}
                    tableId={ClpTableEnum.Deviations}
                    pagedDataApiEndpoint={apiEndpoints.deviation.getpagedactive}
                    nonPagedDataApiEndpoint={apiEndpoints.deviation.getfiltered}
                    refreshKey={refreshKey}
                  />
                </Box>
              )}
              {visitedViewsByTab.general.has(DeviationView.Completed) && (
                <Box sx={{ display: viewByTab.general === DeviationView.Completed ? "block" : "none" }}>
                  <ClpTableServerSidePaging
                    onRowClick={(row: any) => history.push(allRoutes.DeviationListDetails(row.original.id))}
                    columns={initialColumnData}
                    tableId={ClpTableEnum.Deviations}
                    pagedDataApiEndpoint={apiEndpoints.deviation.getpagedcompleted}
                    nonPagedDataApiEndpoint={apiEndpoints.deviation.getfilteredcompleted}
                    refreshKey={refreshKey}
                  />
                </Box>
              )}
            </TabPanel>
          </Box>
        )}

        {visitedTabs.has("vehicledamage") && (
          <Box
            sx={{
              display:
                tabs.find((tab) => tab.value === "vehicledamage")?.index === tabValue ? "block" : "none"
            }}>
            <TabPanel
              value={tabValue}
              key={1}
              index={tabs.find((tab) => tab.value === "vehicledamage")?.index || 1}>
              <Box px={{ xs: 2, md: 3 }} pt={{ xs: 2, md: 3 }} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                <NumberTypeSelector
                  value={numberType}
                  onChange={setNumberType}
                  label={t("global.show_reg_int_nr", "Show Reg. No./Internal No.")}
                  options={[
                    { value: "reg", label: t("global.reg_number_short", "Reg No") },
                    { value: "intern", label: t("global.internal_number_short", "Intern No") },
                  ]}
                />
                <ViewToggler
                  setView={handleViewChange("vehicleDamage")}
                  view={viewByTab.vehicleDamage}
                  viewList={viewList(t)}
                />
              </Box>
              {visitedViewsByTab.vehicleDamage.has(DeviationView.Active) && (
                <Box sx={{ display: viewByTab.vehicleDamage === DeviationView.Active ? "block" : "none" }}>
                  <ClpTableServerSidePaging
                    onRowClick={(row: any) => history.push(allRoutes.DeviationListDetails(row.original.id))}
                    columns={columnsByTab.vehicleDamage}
                    tableId={ClpTableEnum.Deviations}
                    pagedDataApiEndpoint={apiEndpoints.deviation.getpagedvehicledamageactive}
                    nonPagedDataApiEndpoint={apiEndpoints.deviation.getfilteredvehicledamage}
                    refreshKey={refreshKey}
                  />
                </Box>
              )}
              {visitedViewsByTab.vehicleDamage.has(DeviationView.Completed) && (
                <Box sx={{ display: viewByTab.vehicleDamage === DeviationView.Completed ? "block" : "none" }}>
                  <ClpTableServerSidePaging
                    onRowClick={(row: any) => history.push(allRoutes.DeviationListDetails(row.original.id))}
                    columns={columnsByTab.vehicleDamage}
                    tableId={ClpTableEnum.Deviations}
                    pagedDataApiEndpoint={apiEndpoints.deviation.getpagedvehicledamagecompleted}
                    nonPagedDataApiEndpoint={apiEndpoints.deviation.getfilteredvehicledamagecompleted}
                    refreshKey={refreshKey}
                  />
                </Box>
              )}
            </TabPanel>
          </Box>
        )}

        {visitedTabs.has("trafficlog") && (
          <Box
            sx={{
              display: tabs.find((tab) => tab.value === "trafficlog")?.index === tabValue ? "block" : "none"
            }}>
            <TabPanel
              value={tabValue}
              key={2}
              index={tabs.find((tab) => tab.value === "trafficlog")?.index || 2}>
              <Box px={{ xs: 2, md: 3 }} pt={{ xs: 2, md: 3 }}>
                <ViewToggler
                  setView={handleViewChange("trafficLog")}
                  view={viewByTab.trafficLog}
                  viewList={viewList(t)}
                />
              </Box>
              {visitedViewsByTab.trafficLog.has(DeviationView.Active) && (
                <Box sx={{ display: viewByTab.trafficLog === DeviationView.Active ? "block" : "none" }}>
                  <ClpTableServerSidePaging
                    onRowClick={(row: any) => {
                      handleRowClick(row)
                    }}
                    columns={columnsByTab.trafficLog}
                    tableId={ClpTableEnum.Deviations}
                    pagedDataApiEndpoint={apiEndpoints.deviation.getpagedtrafficlogactive}
                    nonPagedDataApiEndpoint={apiEndpoints.deviation.getfilteredtrafficlog}
                    refreshKey={refreshKey}
                  />
                </Box>
              )}
              {visitedViewsByTab.trafficLog.has(DeviationView.Completed) && (
                <Box sx={{ display: viewByTab.trafficLog === DeviationView.Completed ? "block" : "none" }}>
                  <ClpTableServerSidePaging
                    onRowClick={(row: any) => {
                      handleRowClick(row)
                    }}
                    columns={columnsByTab.trafficLog}
                    tableId={ClpTableEnum.Deviations}
                    pagedDataApiEndpoint={apiEndpoints.deviation.getpagedtrafficlogcompleted}
                    nonPagedDataApiEndpoint={apiEndpoints.deviation.getfilteredtrafficlogcompleted}
                    refreshKey={refreshKey}
                  />
                </Box>
              )}
            </TabPanel>
          </Box>
        )}
      </StyledTableContainer>
    </div>
  )
}

export default DeviationList
